// src/features/clanWatches/useClanWatches.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../../lib/api';

export interface ClanWatch {
  id: number;
  clan_name: string;
  vault_channel_id: string | null;
  events_channel_id: string | null;
  other_channel_id: string | null;
}

export interface ClanWatchInput {
  clanName: string;
  vaultChannelId: string | null;
  eventsChannelId: string | null;
  otherChannelId: string | null;
}

export function useClanWatches(guildId: string | null) {
  return useQuery({
    queryKey: ['clan-watches', guildId],
    queryFn: () => apiFetch<ClanWatch[]>(`/api/clan-watches/${guildId}`),
    enabled: !!guildId,
  });
}

export function useCreateClanWatch(guildId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ClanWatchInput) =>
      apiFetch(`/api/clan-watches/${guildId}`, { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['clan-watches', guildId] }),
  });
}

export function useUpdateClanWatch(guildId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: ClanWatchInput }) =>
      apiFetch(`/api/clan-watches/${guildId}/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['clan-watches', guildId] }),
  });
}

export function useDeleteClanWatch(guildId: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => apiFetch(`/api/clan-watches/${guildId}/${id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['clan-watches', guildId] }),
  });
}
